import { ID, Permissions } from "@nova/constants"
import { selectFireById } from "features/entity-caching/fires/selectors"
import {
    selectAllWorkspaceMembers,
    selectWorkspaceMembers,
} from "features/entity-caching/members/UserManagementSlice"
import { selectActivatedFire } from "features/map/control/selectors"
import { useAppSelector } from "integrations/redux/hooks"
import { useMemo } from "react"
import { selectUserID } from "user/profile/selectors"
import { selectActiveWorkspace } from "./WorkspaceManagementSlice"
import { isLocalDevelopmentEnvironment } from "tools/debugging/module"
import { MemberRecord } from "@nova/schemas"
import { retrievePermissionsFromTagTree } from "@nova/util-tag-tree"
import { useParams } from "react-router"

const debugPermissions = isLocalDevelopmentEnvironment() && false
export const useWorkspacePermissions = (entityId?: string) => {
    const selfId = useAppSelector(selectUserID) || ""
    const members = useAppSelector(selectWorkspaceMembers)
    const { projectId } = useParams<{ projectId?: string }>()

    const adminCount = useMemo(() => {
        let count = 0
        members?.forEach((member) => {
            if (member.permissions.workspaceAccess === Permissions.Admin) count++
        })
        return count
    }, [members])

    const { permissions, isLoading } = useMemberPermissions(selfId, entityId || projectId)

    const isAdmin = permissions === Permissions.Admin
    const isEditor = permissions === Permissions.Editor || isAdmin
    /** @deprecated If the backend lets a user see something, this check is redundant and usually just causes problems. */
    const isViewer = permissions === Permissions.Viewer || isEditor

    if (debugPermissions)
        return {
            isViewer: true,
            isEditor: true,
            isAdmin: true,
            adminCount,
            selfId,
            members,
        }

    return {
        isViewer,
        isEditor,
        isAdmin,
        selfId,
        adminCount,
        members,
        isLoading,
    }
}

export const useMemberPermissions = (memberId?: ID, entityId?: ID) => {
    const activeWorkspace = useAppSelector(selectActiveWorkspace())
    const activeProject = useAppSelector(selectActivatedFire)
    const selectedProject = useAppSelector(selectFireById(entityId))
    const userId = useAppSelector(selectUserID)
    const members = useAppSelector(selectAllWorkspaceMembers(activeWorkspace?.id || ""))

    const member = useMemo(() => {
        const id = memberId || userId
        return id && members?.find((member) => member.userId === id)
    }, [members, userId, memberId]) as MemberRecord | undefined

    //walk up the access path and search for the most immediate permissions granted
    const permissions =
        member && activeWorkspace
            ? retrievePermissionsFromTagTree(
                  member.permissions,
                  activeWorkspace.tagTree,
                  selectedProject || activeProject,
                  entityId
              )
            : Permissions.Viewer

    return {
        permissions,
        member,
        isLoading: member === undefined || activeWorkspace === undefined,
    }
}
